import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const itemFade = {
  hidden: {
    opacity: 0,
    x: -20
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5
    }
  }
};
// ─── REUSABLE SECTION WRAPPER ──────────────────────────────────────────────
function Section({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-80px'
  }} variants={fadeUp} className={`py-16 md:py-24 px-4 ${className}`} data-id="element-386">
      {children}
    </motion.section>;
}
function SectionTitle({
  children,
  gold = false
}: {
  children: React.ReactNode;
  gold?: boolean;
}) {
  return <h2 className={`font-heading text-3xl md:text-5xl font-bold uppercase text-center mb-4 leading-tight ${gold ? 'text-gold-light' : 'text-white'}`} data-id="element-387">
      {children}
    </h2>;
}
// ─── URGENCY BAR ──────────────────────────────────────────────────────────
function UrgencyBar() {
  return <div className="w-full bg-gold-dark py-2 px-4 text-center" data-id="element-388">
      <p className="font-heading text-navy text-sm md:text-base font-bold uppercase tracking-widest" data-id="element-389">
        ⚡ Oferta Especial · Acceso Inmediato · Precio Solo por Tiempo Limitado
        ⚡
      </p>
    </div>;
}
// ─── HERO ─────────────────────────────────────────────────────────────────
function HeroSection() {
  return <section className="relative bg-gradient-to-b from-[#060e1a] via-[#0B1628] to-[#132040] pt-12 pb-20 px-4 overflow-hidden" data-id="element-390">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-391">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-gold/5 rounded-full blur-3xl" data-id="element-392" />
      </div>

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-393">
        {/* Badge */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="flex justify-center mb-6" data-id="element-394">
          <span className="inline-block bg-gold/10 border border-gold/40 text-gold-light font-heading text-xs md:text-sm uppercase tracking-widest px-4 py-2 rounded-full" data-id="element-395">
            🛡️ Guía Exclusiva para Inmigrantes con ITIN
          </span>
        </motion.div>

        {/* H1 */}
        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.1
      }} className="font-heading text-4xl md:text-6xl lg:text-7xl font-extrabold uppercase text-center leading-tight mb-6" data-id="element-396">
          <span className="text-white" data-id="element-397">El Blindaje</span>{' '}
          <span className="text-gold-light" data-id="element-398">Financiero:</span>
          <br data-id="element-399" />
          <span className="text-white text-3xl md:text-4xl lg:text-5xl" data-id="element-400">
            Cómo Usar Lightning e ITIN para
          </span>
          <br data-id="element-401" />
          <span className="text-gold-light" data-id="element-402">Dominar tu Crédito en USA</span>
        </motion.h1>

        {/* H2 */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.2
      }} className="text-center text-white/80 text-lg md:text-xl max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-403">
          La Guía Paso a Paso para construir un{' '}
          <strong className="text-gold-light" data-id="element-404">Score de 700+</strong> sin Seguro
          Social, activar tu reporte en tiempo récord y dejar de ser invisible
          para los bancos.
        </motion.p>

        {/* Product Image */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} className="flex justify-center mb-10 float-animation" data-id="element-405">
          <img src="https://cdn.magicpatterns.com/uploads/eSL29J3qhDYYddxbkk3CGw/producto_2.png" alt="El Blindaje del Crédito - Guía Digital" className="w-full max-w-2xl rounded-2xl shadow-2xl" style={{
          filter: 'drop-shadow(0 0 40px rgba(212,175,55,0.3))'
        }} data-id="element-406" />
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.5
      }} className="flex flex-col items-center gap-4" data-id="element-407">
          <a href="https://foryoulife.shop/cart/45019979808821:1" target="_blank" rel="noopener noreferrer" className="cta-pulse gold-gradient text-navy font-heading text-xl md:text-2xl font-extrabold uppercase px-10 py-5 rounded-xl tracking-wide hover:scale-105 transition-transform duration-200 w-full max-w-xl inline-block text-center no-underline" data-id="element-408">
            ¡SÍ! QUIERO EMPEZAR A SUMAR PUNTOS HOY MISMO
          </a>
          <p className="text-white/60 text-sm" data-id="element-409">
            🔒 Pago 100% seguro y descarga inmediata
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-white/70 text-sm" data-id="element-410">
            <span data-id="element-411">✅ Pago Único $27</span>
            <span data-id="element-412">✅ Acceso Inmediato</span>
            <span data-id="element-413">✅ 100% Legal</span>
            <span data-id="element-414">✅ Actualizado 2024</span>
          </div>
        </motion.div>
      </div>
    </section>;
}
// ─── HOOK ─────────────────────────────────────────────────────────────────
function HookSection() {
  return <Section className="bg-[#0B1628]" data-id="element-415">
      <div className="max-w-3xl mx-auto text-center" data-id="element-416">
        <p className="text-white/90 text-lg md:text-xl leading-relaxed mb-6" data-id="element-417">
          Viniste a este país a progresar, pero te encontraste con una{' '}
          <strong className="text-gold-light" data-id="element-418">pared invisible</strong>: el
          sistema crediticio. Sin un Social Security, parece que las puertas de
          las rentas bajas, los préstamos de auto al 0% y las tarjetas de
          crédito están cerradas para ti.
        </p>
        <div className="border-l-4 border-gold pl-6 text-left bg-navy-light/50 rounded-r-xl py-4 pr-4" data-id="element-419">
          <p className="text-gold-light text-lg md:text-xl font-semibold leading-relaxed" data-id="element-420">
            "Pero el sistema tiene un secreto que pocos inmigrantes conocen. Tu
            ITIN no es solo para pagar impuestos; es tu herramienta más poderosa
            para <em data-id="element-421">"hackear" legalmente</em> el sistema bancario
            estadounidense y empezar a sumar puntos desde hoy mismo."
          </p>
        </div>
      </div>
    </Section>;
}
// ─── PROBLEM ──────────────────────────────────────────────────────────────
function ProblemSection() {
  const stats = [{
    value: '$3,000',
    label: 'Depósito promedio sin historial',
    icon: '🏠'
  }, {
    value: '2x',
    label: 'Más intereses que pagas vs. residentes',
    icon: '💸'
  }, {
    value: '0 pts',
    label: 'Lo que el sistema ve de ti ahora',
    icon: '📊'
  }];
  return <Section className="bg-gradient-to-b from-[#0B1628] to-[#132040]" data-id="element-422">
      <div className="max-w-5xl mx-auto" data-id="element-423">
        <SectionTitle gold data-id="element-424">
          La Exclusión Financiera: El Muro Invisible que te Está Costando Miles
        </SectionTitle>
        <p className="text-center text-white/70 text-base md:text-lg max-w-2xl mx-auto mb-12" data-id="element-425">
          Millones de latinos en EE.UU. tienen ITIN pero no SSN.
        </p>

        {/* Stats */}
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-426">
          {stats.map(s => <motion.div key={s.value} variants={itemFade} className="navy-card rounded-2xl p-8 text-center" data-id="element-427">
              <div className="text-4xl mb-3" data-id="element-428">{s.icon}</div>
              <div className="font-heading text-5xl font-extrabold text-gold-light mb-2" data-id="element-429">
                {s.value}
              </div>
              <p className="text-white/70 text-sm" data-id="element-430">{s.label}</p>
            </motion.div>)}
        </motion.div>

        <div className="max-w-3xl mx-auto bg-red-900/20 border border-red-500/30 rounded-2xl p-6 md:p-8" data-id="element-431">
          <p className="text-white/90 text-base md:text-lg leading-relaxed" data-id="element-432">
            El problema no es tu falta de dinero,{' '}
            <strong className="text-red-400" data-id="element-433">es tu falta de historial</strong>.
            Sin un reporte que hable por ti ante Equifax, Experian y TransUnion,
            el sistema te ignora. Estás trabajando duro, pero tu esfuerzo no se
            refleja en los burós de crédito.
          </p>
        </div>
      </div>
    </Section>;
}
// ─── WHY FAILED ───────────────────────────────────────────────────────────
function WhyFailedSection() {
  const items = [{
    icon: '🏦',
    title: 'Esperar a que el banco te llame',
    desc: 'Los bancos tradicionales rara vez te darán una oportunidad si no tienes historial previo.'
  }, {
    icon: '💰',
    title: 'Pagar a "tramitadores" o reparadores de crédito',
    desc: 'Te cobran cuotas por adelantado (red flag) por algo que tú puedes hacer legalmente.'
  }, {
    icon: '💵',
    title: 'Usar efectivo para todo',
    desc: 'El efectivo no deja rastro; para el sistema, si no hay deuda reportada, simplemente no existes.'
  }, {
    icon: '📋',
    title: 'Creer que el ITIN no sirve para crédito',
    desc: 'El ITIN es un documento fiscal válido para solicitar productos financieros en plataformas específicas como Lightning.'
  }];
  return <Section className="bg-[#132040]" data-id="element-434">
      <div className="max-w-5xl mx-auto" data-id="element-435">
        <SectionTitle data-id="element-436">
          Por Qué Lo Que Has Intentado No Ha Funcionado
        </SectionTitle>
        <p className="text-center text-white/60 mb-12" data-id="element-437">
          No es tu culpa. El sistema está diseñado para mantenerte fuera.
        </p>
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-438">
          {items.map(item => <motion.div key={item.title} variants={itemFade} className="flex gap-4 bg-red-900/10 border border-red-500/20 rounded-2xl p-6" data-id="element-439">
              <div className="text-3xl flex-shrink-0" data-id="element-440">{item.icon}</div>
              <div data-id="element-441">
                <div className="flex items-center gap-2 mb-2" data-id="element-442">
                  <span className="text-red-400 font-bold text-lg" data-id="element-443">✗</span>
                  <h3 className="font-heading text-white text-lg font-bold uppercase" data-id="element-444">
                    {item.title}
                  </h3>
                </div>
                <p className="text-white/70 text-sm leading-relaxed" data-id="element-445">
                  {item.desc}
                </p>
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </Section>;
}
// ─── MECHANISM ────────────────────────────────────────────────────────────
function MechanismSection() {
  const bureaus = ['Equifax', 'Experian', 'TransUnion'];
  return <Section className="bg-gradient-to-b from-[#132040] to-[#0B1628]" data-id="element-446">
      <div className="max-w-4xl mx-auto text-center" data-id="element-447">
        <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/30 px-4 py-2 rounded-full mb-6" data-id="element-448">
          <span className="text-gold-light font-heading text-sm uppercase tracking-widest" data-id="element-449">
            La Solución
          </span>
        </div>
        <SectionTitle gold data-id="element-450">El Método de Apalancamiento Lightning</SectionTitle>

        {/* Shield Icon */}
        <motion.div initial={{
        scale: 0.5,
        opacity: 0
      }} whileInView={{
        scale: 1,
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        type: 'spring'
      }} className="flex justify-center my-8" data-id="element-451">
          <div className="w-32 h-32 rounded-full flex items-center justify-center text-6xl" style={{
          background: 'linear-gradient(135deg, #D4AF37, #F0C040)',
          boxShadow: '0 0 60px rgba(212,175,55,0.5)'
        }} data-id="element-452">
            🛡️
          </div>
        </motion.div>

        <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-6 max-w-3xl mx-auto" data-id="element-453">
          Lightning no es un banco tradicional;{' '}
          <strong className="text-gold-light" data-id="element-454">
            es un puente financiero diseñado para personas con ITIN.
          </strong>{' '}
          Nuestra guía te enseña a navegar este ecosistema para que cada dólar
          que muevas sume puntos positivos.
        </p>
        <p className="text-white/70 text-base leading-relaxed mb-10 max-w-2xl mx-auto" data-id="element-455">
          Usamos la{' '}
          <strong className="text-gold-light" data-id="element-456">
            regla del 30% de utilización
          </strong>{' '}
          y el reporte directo a los burós para que, en lugar de solo pedir
          dinero, estés construyendo un "Escudo Crediticio" que te permita
          comprar tu casa o auto con tasas de interés de residente.
        </p>

        {/* Bureaus */}
        <div className="flex flex-wrap justify-center gap-4" data-id="element-457">
          {bureaus.map(b => <div key={b} className="bg-navy-light border border-gold/30 rounded-xl px-6 py-3 font-heading text-gold-light font-bold text-lg uppercase tracking-wide" data-id="element-458">
              {b}
            </div>)}
        </div>
        <p className="text-white/50 text-sm mt-3" data-id="element-459">
          Reporta directamente a los 3 burós principales
        </p>
      </div>
    </Section>;
}
// ─── WHAT YOU GET ─────────────────────────────────────────────────────────
function WhatYouGetSection() {
  const items = [{
    num: '01',
    icon: '📋',
    title: 'El Proceso de 6 Pasos',
    desc: 'Desde la creación de cuenta y verificación de identidad hasta la aprobación de tu primer crédito con ITIN.'
  }, {
    num: '02',
    icon: '📄',
    title: 'La Lista de Requisitos de Oro',
    desc: 'No pierdas tiempo; conoce exactamente qué documentos necesitas (Id oficial, comprobante de domicilio e ingresos).'
  }, {
    num: '03',
    icon: '💳',
    title: 'Los 7 Hábitos del Score Alto',
    desc: 'La estrategia de pagos automáticos y presupuesto 50/30/20 para blindar tu historial.'
  }, {
    num: '04',
    icon: '🚨',
    title: 'Guía Anti-Estafas',
    desc: 'Cómo detectar "Red Flags" y proteger tu información personal de falsos gestores.'
  }, {
    num: '05',
    icon: '📊',
    title: 'Escenarios de Éxito',
    desc: 'El desglose del "Caso de María" para que entiendas cómo pasar de cero a 650+ puntos en menos de un año.'
  }];
  return <Section className="bg-[#0B1628]" data-id="element-460">
      <div className="max-w-4xl mx-auto" data-id="element-461">
        <SectionTitle gold data-id="element-462">Todo Lo Que Incluye Tu Guía</SectionTitle>
        <p className="text-center text-white/60 mb-12 text-lg" data-id="element-463">
          Un sistema completo, no solo información suelta.
        </p>
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="space-y-4" data-id="element-464">
          {items.map(item => <motion.div key={item.num} variants={itemFade} className="flex items-start gap-5 navy-card rounded-2xl p-6 hover:border-gold/40 transition-colors duration-300" data-id="element-465">
              <div className="font-heading text-4xl font-extrabold text-gold/30 flex-shrink-0 w-12 text-center" data-id="element-466">
                {item.num}
              </div>
              <div className="text-3xl flex-shrink-0" data-id="element-467">{item.icon}</div>
              <div data-id="element-468">
                <h3 className="font-heading text-white text-xl font-bold uppercase mb-1" data-id="element-469">
                  {item.title}
                </h3>
                <p className="text-white/70 text-sm leading-relaxed" data-id="element-470">
                  {item.desc}
                </p>
              </div>
              <div className="ml-auto flex-shrink-0" data-id="element-471">
                <span className="text-gold-light text-2xl" data-id="element-472">✓</span>
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </Section>;
}
// ─── WHO IS FOR ───────────────────────────────────────────────────────────
function WhoIsForSection() {
  const forItems = ['Inmigrantes con ITIN que quieren dejar de pagar depósitos altos.', 'Padres de familia que desean calificar para un préstamo de vivienda.', 'Trabajadores independientes que necesitan financiamiento para su negocio.', 'Cualquier persona cansada de que le digan "no tienes suficiente crédito".'];
  const notForItems = ['Personas que buscan dinero "gratis" sin intención de pagar.', 'Quienes no están dispuestos a ser disciplinados con sus fechas de pago.', 'Personas que ya tienen un Seguro Social y un historial sólido.'];
  return <Section className="bg-[#132040]" data-id="element-473">
      <div className="max-w-5xl mx-auto" data-id="element-474">
        <SectionTitle data-id="element-475">¿Es Esta Guía Para Ti?</SectionTitle>
        <p className="text-center text-white/60 mb-12" data-id="element-476">
          Sé honesto contigo mismo.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-477">
          {/* FOR */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="bg-green-900/20 border border-green-500/30 rounded-2xl p-6 md:p-8" data-id="element-478">
            <h3 className="font-heading text-green-400 text-2xl font-bold uppercase mb-6 flex items-center gap-2" data-id="element-479">
              ✅ Esto ES para ti si...
            </h3>
            <motion.ul variants={stagger} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="space-y-4" data-id="element-480">
              {forItems.map(item => <motion.li key={item} variants={itemFade} className="flex items-start gap-3" data-id="element-481">
                  <span className="text-green-400 text-xl flex-shrink-0 mt-0.5" data-id="element-482">
                    ✓
                  </span>
                  <span className="text-white/85 text-sm leading-relaxed" data-id="element-483">
                    {item}
                  </span>
                </motion.li>)}
            </motion.ul>
          </motion.div>

          {/* NOT FOR */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="bg-red-900/20 border border-red-500/30 rounded-2xl p-6 md:p-8" data-id="element-484">
            <h3 className="font-heading text-red-400 text-2xl font-bold uppercase mb-6 flex items-center gap-2" data-id="element-485">
              ✗ Esto NO es para ti si...
            </h3>
            <motion.ul variants={stagger} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="space-y-4" data-id="element-486">
              {notForItems.map(item => <motion.li key={item} variants={itemFade} className="flex items-start gap-3" data-id="element-487">
                  <span className="text-red-400 text-xl flex-shrink-0 mt-0.5" data-id="element-488">
                    ✗
                  </span>
                  <span className="text-white/85 text-sm leading-relaxed" data-id="element-489">
                    {item}
                  </span>
                </motion.li>)}
            </motion.ul>
          </motion.div>
        </div>
      </div>
    </Section>;
}
// ─── HOW IT WORKS ─────────────────────────────────────────────────────────
function HowItWorksSection() {
  const steps = [{
    num: '1',
    icon: '📥',
    title: 'Descarga la Guía',
    desc: 'Obtienes acceso inmediato al sistema completo en PDF.'
  }, {
    num: '2',
    icon: '⚡',
    title: 'Aplica el Método Lightning',
    desc: 'Sigue los pasos de verificación y solicitud de productos que sí reportan a los burós.'
  }, {
    num: '3',
    icon: '📈',
    title: 'Monitorea tu Crecimiento',
    desc: 'Usa nuestras herramientas recomendadas para ver cómo tu score sube mes a mes.'
  }];
  return <Section className="bg-gradient-to-b from-[#0B1628] to-[#132040]" data-id="element-490">
      <div className="max-w-4xl mx-auto" data-id="element-491">
        <SectionTitle gold data-id="element-492">Así de Simple Funciona</SectionTitle>
        <p className="text-center text-white/60 mb-12" data-id="element-493">
          3 pasos. Sin complicaciones.
        </p>
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-494">
          {steps.map((step, i) => <motion.div key={step.num} variants={itemFade} className="text-center relative" data-id="element-495">
              {i < steps.length - 1 && <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-gold/20 z-0" data-id="element-496" />}
              <div className="w-20 h-20 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 relative z-10" style={{
            background: 'linear-gradient(135deg, #D4AF37, #F0C040)'
          }} data-id="element-497">
                {step.icon}
              </div>
              <div className="font-heading text-gold/40 text-5xl font-extrabold mb-2" data-id="element-498">
                {step.num}
              </div>
              <h3 className="font-heading text-white text-xl font-bold uppercase mb-2" data-id="element-499">
                {step.title}
              </h3>
              <p className="text-white/70 text-sm leading-relaxed" data-id="element-500">
                {step.desc}
              </p>
            </motion.div>)}
        </motion.div>
      </div>
    </Section>;
}
// ─── PROOF / CASE STUDY ───────────────────────────────────────────────────
function ProofSection() {
  const timeline = [{
    month: 'Mes 0',
    score: '0',
    label: 'Sin historial',
    color: 'bg-red-500'
  }, {
    month: 'Mes 3',
    score: '580',
    label: 'Score inicial',
    color: 'bg-yellow-500'
  }, {
    month: 'Mes 6',
    score: '620',
    label: 'Crecimiento',
    color: 'bg-blue-400'
  }, {
    month: 'Mes 12',
    score: '650+',
    label: '¡Objetivo!',
    color: 'bg-green-400'
  }];
  return <Section className="bg-[#132040]" data-id="element-501">
      <div className="max-w-4xl mx-auto" data-id="element-502">
        <div className="text-center mb-12" data-id="element-503">
          <span className="inline-block bg-gold/10 border border-gold/30 text-gold-light font-heading text-xs uppercase tracking-widest px-4 py-2 rounded-full mb-4" data-id="element-504">
            Caso de Éxito Real
          </span>
          <SectionTitle gold data-id="element-505">
            El Caso de María: De Cero a 650+ Puntos
          </SectionTitle>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto" data-id="element-506">
            En solo 6 a 12 meses de aplicar estos hábitos sistemáticamente,
            podrías pasar de no tener historial a poseer un perfil crediticio
            que te ahorre{' '}
            <strong className="text-gold-light" data-id="element-507">
              miles de dólares en intereses
            </strong>
            .
          </p>
        </div>

        {/* Timeline */}
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-508">
          {timeline.map(t => <motion.div key={t.month} variants={itemFade} className="navy-card rounded-2xl p-6 text-center" data-id="element-509">
              <div className={`w-3 h-3 rounded-full ${t.color} mx-auto mb-3`} data-id="element-510" />
              <div className="font-heading text-gold-light text-3xl font-extrabold mb-1" data-id="element-511">
                {t.score}
              </div>
              <div className="text-white/50 text-xs uppercase tracking-wide mb-1" data-id="element-512">
                {t.month}
              </div>
              <div className="text-white/80 text-sm" data-id="element-513">{t.label}</div>
            </motion.div>)}
        </motion.div>

        <p className="text-center text-white/60 text-sm mt-6" data-id="element-514">
          Un solo préstamo pequeño bien manejado abre las puertas a líneas de
          crédito mayores.
        </p>
      </div>
    </Section>;
}
// ─── FAQ ──────────────────────────────────────────────────────────────────
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [{
    q: '¿Es legal pedir crédito con ITIN?',
    a: 'Sí, es 100% legal y está avalado por las instituciones financieras participantes. El ITIN es un número de identificación fiscal emitido por el IRS y es aceptado por múltiples instituciones financieras.'
  }, {
    q: '¿Lightning reporta a los burós?',
    a: 'Sí, dependiendo del producto seleccionado, reporta a Equifax, Experian o TransUnion. Esto es fundamental para construir tu historial crediticio.'
  }, {
    q: '¿Necesito ganar mucho dinero?',
    a: 'No, los requisitos se basan en tu capacidad de pago y estabilidad, no solo en el monto. Lo importante es demostrar consistencia en tus ingresos.'
  }, {
    q: '¿Cuánto tiempo tardan los resultados?',
    a: 'Generalmente de 3 a 6 meses para generar un score inicial. Con disciplina y los hábitos correctos, puedes ver mejoras significativas en 6 a 12 meses.'
  }];
  return <Section className="bg-[#0B1628]" data-id="element-515">
      <div className="max-w-3xl mx-auto" data-id="element-516">
        <SectionTitle data-id="element-517">Preguntas Frecuentes</SectionTitle>
        <p className="text-center text-white/60 mb-10" data-id="element-518">
          Resolvemos tus dudas más comunes.
        </p>
        <div className="space-y-3" data-id="element-519">
          {faqs.map((faq, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="navy-card rounded-xl overflow-hidden" data-id="element-520">
              <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors duration-200" data-id="element-521">
                <span className="font-heading text-white text-base md:text-lg font-bold uppercase pr-4" data-id="element-522">
                  {faq.q}
                </span>
                <span className="text-gold-light text-2xl flex-shrink-0 transition-transform duration-300" style={{
              transform: openIndex === i ? 'rotate(45deg)' : 'rotate(0deg)'
            }} data-id="element-523">
                  +
                </span>
              </button>
              <AnimatePresence data-id="element-524">
                {openIndex === i && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-525">
                    <div className="px-5 pb-5 text-white/75 text-sm leading-relaxed border-t border-white/10 pt-4" data-id="element-526">
                      {faq.a}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>
      </div>
    </Section>;
}
// ─── PRICE BLOCK ──────────────────────────────────────────────────────────
function PriceBlock() {
  const features = ['El Proceso de 6 Pasos con ITIN', 'Lista de Requisitos de Oro', 'Los 7 Hábitos del Score Alto', 'Guía Anti-Estafas completa', 'Caso de Éxito "María" detallado', 'Actualizaciones 2024 incluidas', 'Acceso instantáneo en PDF'];
  return <Section className="bg-gradient-to-b from-[#132040] to-[#060e1a]" data-id="element-527">
      <div className="max-w-2xl mx-auto" data-id="element-528">
        {/* Pre-headline */}
        <p className="text-center text-white/60 text-base mb-4" data-id="element-529">
          No pagues $500 por una asesoría que te dirá la mitad de esto.
        </p>
        <SectionTitle gold data-id="element-530">
          Toma el Control de tu Futuro Financiero Hoy Mismo
        </SectionTitle>

        {/* Price Card */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mt-10 rounded-3xl overflow-hidden" style={{
        background: 'linear-gradient(135deg, #132040, #1a2d52)',
        border: '2px solid rgba(212,175,55,0.5)',
        boxShadow: '0 0 60px rgba(212,175,55,0.15)'
      }} data-id="element-531">
          {/* Card Header */}
          <div className="gold-gradient p-4 text-center" data-id="element-532">
            <p className="font-heading text-navy text-sm font-bold uppercase tracking-widest" data-id="element-533">
              ⚡ Oferta Especial — Precio Único
            </p>
          </div>

          <div className="p-8 md:p-10" data-id="element-534">
            {/* Product Name */}
            <h3 className="font-heading text-white text-2xl md:text-3xl font-bold uppercase text-center mb-6" data-id="element-535">
              Guía Completa de Crédito con ITIN
            </h3>

            {/* Features */}
            <ul className="space-y-3 mb-8" data-id="element-536">
              {features.map(f => <li key={f} className="flex items-center gap-3" data-id="element-537">
                  <span className="text-gold-light text-lg" data-id="element-538">✓</span>
                  <span className="text-white/85 text-sm" data-id="element-539">{f}</span>
                </li>)}
            </ul>

            {/* Price */}
            <div className="text-center mb-8" data-id="element-540">
              <div className="text-white/40 text-2xl line-through mb-1" data-id="element-541">
                $97.00 USD
              </div>
              <div className="font-heading text-7xl font-extrabold" style={{
              background: 'linear-gradient(135deg, #D4AF37, #F0C040)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }} data-id="element-542">
                $27
              </div>
              <div className="text-gold-light text-lg font-semibold" data-id="element-543">USD</div>
              <p className="text-white/50 text-sm mt-2" data-id="element-544">
                Pago único. Acceso instantáneo. Actualizaciones 2024 incluidas.
              </p>
            </div>

            {/* CTA Button */}
            <a href="https://foryoulife.shop/cart/45019979808821:1" target="_blank" rel="noopener noreferrer" className="cta-pulse w-full gold-gradient text-navy font-heading text-xl md:text-2xl font-extrabold uppercase px-8 py-5 rounded-xl tracking-wide hover:scale-105 transition-transform duration-200 inline-block text-center no-underline" data-id="element-545">
              ¡SÍ! QUIERO EMPEZAR A SUMAR PUNTOS HOY MISMO
            </a>

            <p className="text-center text-white/50 text-sm mt-4" data-id="element-546">
              🔒 Pago 100% seguro y descarga inmediata
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-3 mt-6" data-id="element-547">
              {['✅ Pago Seguro', '✅ Acceso Inmediato', '✅ 100% Legal', '✅ Garantía de Satisfacción'].map(badge => <span key={badge} className="bg-white/5 border border-white/10 text-white/70 text-xs px-3 py-1.5 rounded-full" data-id="element-548">
                  {badge}
                </span>)}
            </div>
          </div>
        </motion.div>
      </div>
    </Section>;
}
// ─── FOOTER ───────────────────────────────────────────────────────────────
function Footer() {
  return <footer className="bg-[#060e1a] border-t border-white/10 py-8 px-4 text-center" data-id="element-549">
      <p className="text-white/30 text-xs" data-id="element-550">
        © 2024 El Blindaje Financiero. Todos los derechos reservados.
        <br data-id="element-551" />
        Este producto es de carácter educativo. Los resultados pueden variar.
      </p>
    </footer>;
}
// ─── MAIN PAGE ────────────────────────────────────────────────────────────
export function SalesPage() {
  return <div className="min-h-screen bg-[#0B1628]" data-id="element-552">
      <UrgencyBar data-id="element-553" />
      <HeroSection data-id="element-554" />
      <HookSection data-id="element-555" />
      <ProblemSection data-id="element-556" />
      <WhyFailedSection data-id="element-557" />
      <MechanismSection data-id="element-558" />
      <WhatYouGetSection data-id="element-559" />
      <WhoIsForSection data-id="element-560" />
      <HowItWorksSection data-id="element-561" />
      <ProofSection data-id="element-562" />
      <FAQSection data-id="element-563" />
      <PriceBlock data-id="element-564" />
      <Footer data-id="element-565" />
    </div>;
}